import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiContentManager, { INFO_ROOT_SLUG } from 'services/api/ApiContentManager';
import { CategoryPageDTO } from 'services/api/models/CategoryDTO';

/**
 * A page read a few minutes ago shows again at once from the cache, while a
 * fresh copy is fetched behind it (the default stale time): reading an
 * article changes the counts of every page it belongs to.
 */
const GC_TIME = 10 * 60 * 1000;

export const categoryQueryKey = (slug: string) => ['content', 'category', slug] as const;
export const infoRootQueryKey = categoryQueryKey(INFO_ROOT_SLUG);

/** One category page (the root `infos` page carries the home's data) */
export function useCategoryPage(slug: string) {
  return useQuery({
    queryKey: categoryQueryKey(slug),
    queryFn: () => ApiContentManager.getCategory(slug),
    gcTime: GC_TIME,
  });
}

/** The categories whose name or synonyms match `query` */
export function useCategorySearch(query: string) {
  return useQuery({
    queryKey: ['content', 'categories', 'search', query] as const,
    queryFn: () => ApiContentManager.searchCategories(query),
    enabled: query.length > 0,
    gcTime: GC_TIME,
  });
}

/** The articles matching `query`; with no query, the personalised list */
export function usePostSearch(query: string | undefined) {
  return useQuery({
    queryKey: ['content', 'posts', 'search', query ?? ''] as const,
    queryFn: () => ApiContentManager.searchPosts(query, undefined),
    gcTime: GC_TIME,
  });
}

/**
 * Bookmarks or unbookmarks a category: the star flips at once, flips back if
 * the request fails, and the home (which lists the favourites) is refetched.
 */
export function useBookmarkCategory(slug: string) {
  const queryClient = useQueryClient();
  const queryKey = categoryQueryKey(slug);
  return useMutation({
    mutationFn: (bookmarked: boolean) => ApiContentManager.setCategoryBookmark(slug, bookmarked),
    onMutate: async bookmarked => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CategoryPageDTO>(queryKey);
      queryClient.setQueryData<CategoryPageDTO>(
        queryKey,
        current => current && { ...current, is_bookmarked: bookmarked },
      );
      return { previous };
    },
    onError: (_error, _bookmarked, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: infoRootQueryKey }),
  });
}
